import React, { useEffect, useState } from "react";
import { handleDownload, handleDelete } from "../downloadAndDelete";
import {QRCodeSVG} from "qrcode.react";
import "./explore.css";

export function Explore() {
    const [uploads, setUploads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState("");
    const [socket, setSocket] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all' or 'my-files'
    const [currentUser, setCurrentUser] = useState(localStorage.getItem("userName"));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        field: 'date',
        direction: 'desc'
    });
    const [showQRCode, setShowQRCode] = useState(false);
    const [qrCodeData, setQrCodeData] = useState({ url: '', fileName: '' });
    const [copiedLink, setCopiedLink] = useState('');
    const [anonymousId, setAnonymousId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [expandedItems, setExpandedItems] = useState(new Set());

    // Check authentication status and generate anonymous ID
    useEffect(() => {
        setIsAuthenticated(!!currentUser);
        
        // Generate anonymous ID for non-authenticated users
        if (!currentUser) {
            const randomNum = Math.floor(1000 + Math.random() * 9000); // 4 random digits
            setAnonymousId(`Anonymous${randomNum}`);
        }
    }, [currentUser]);

    // If user is not authenticated, force them to "all" tab
    useEffect(() => {
        if (!isAuthenticated && activeTab === 'my-files') {
            setActiveTab('all');
        }
        // Reset to first page when changing tabs
        setCurrentPage(1);
        setExpandedItems(new Set());
    }, [isAuthenticated, activeTab]);

    useEffect(() => {
        // Establish WebSocket connection for all users
        const protocol = window.location.protocol === 'http:' ? 'ws' : 'wss';
        // In dev (port 3000), connect directly to backend on 4000.
        // In production, nginx proxies WS through the same host:port.
        const wsPort = window.location.port === '3000' ? '4000' : window.location.port;
        const wsHost = window.location.hostname + (wsPort ? `:${wsPort}` : '');
        const wsUrl = `${protocol}://${wsHost}`;
        
        const newSocket = new WebSocket(wsUrl);

        newSocket.addEventListener("open", () => {
            setSocket(newSocket);
        });

        newSocket.addEventListener("message", (event) => {
            try {
                const msg = JSON.parse(event.data);
                displayMsg('player-messages', msg.from, msg.value);
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        });

        newSocket.addEventListener("error", (error) => {
            console.error('WebSocket error:', error);
        });

        newSocket.addEventListener("close", (event) => {
            console.log("WebSocket connection closed:", event);
            setSocket(null);
        });

        return () => {
            if (newSocket.readyState === WebSocket.OPEN) {
                newSocket.close();
            }
        };
    }, []); // Remove isAuthenticated dependency

    const displayMsg = (cls, from, msg) => {
        setMessages(prevMessages => [
            { cls, from, msg },
            ...prevMessages,
        ]);
    };

    const handleSendMessage = (event) => {
        event.preventDefault();
        const message = messageInput.trim();
        if (message !== "") {
            const username = currentUser || anonymousId;
            broadcastEvent(username, message);
            setMessageInput("");
        }
    };

    const broadcastEvent = (from, value) => {
        const event = {
            from: from,
            value: value,
        };
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(event));
        } else {
            console.error('WebSocket connection not open. Message not sent.');
            // Optionally show user feedback
            alert('Chat connection not available. Please refresh the page.');
        }
    };

    // Sorting function
    const sortUploads = (uploads, field, direction) => {
        return [...uploads].sort((a, b) => {
            let aValue, bValue;
            
            if (field === 'date') {
                // Convert date strings to Date objects for proper sorting
                aValue = new Date(a.date);
                bValue = new Date(b.date);
            } else if (field === 'count') {
                // Convert download count to numbers
                aValue = parseInt(a.count) || 0;
                bValue = parseInt(b.count) || 0;
            } else {
                // String comparison for other fields
                aValue = a[field] || '';
                bValue = b[field] || '';
            }
            
            if (direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    // Handle sort button clicks
    const handleSort = (field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Show QR code overlay for download link
    const showQRCodeOverlay = (downloadLink, fileName) => {
        const { protocol, hostname } = window.location;
        const downloadUrl = `${protocol}//${hostname}/api/download/${downloadLink}`;
        setQrCodeData({ url: downloadUrl, fileName });
        setShowQRCode(true);
    };

    // Copy download link to clipboard
    const handleCopyLink = (downloadLink) => {
        const { protocol, hostname } = window.location;
        const downloadUrl = `${protocol}//${hostname}/api/download/${downloadLink}`;
        navigator.clipboard.writeText(downloadUrl).then(() => {
            setCopiedLink(downloadLink);
            setTimeout(() => setCopiedLink(''), 2000);
        });
    };

    // Toggle keepForever on a file
    const handleKeepForever = async (upload, keep) => {
        await fetch(`/api/file/${upload.downloadLink}/keep`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ keepForever: keep }),
        });
        setUploads(prev => prev.map(u =>
            u.downloadLink === upload.downloadLink ? { ...u, keepForever: keep } : u
        ));
    };

    // Close QR code overlay
    const closeQRCodeOverlay = () => {
        setShowQRCode(false);
        setQrCodeData({ url: '', fileName: '' });
    };

    // Pagination functions
    const getCurrentPageData = (data) => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return data.slice(startIndex, endIndex);
    };

    // Expand/collapse item details
    const toggleItemExpansion = (itemId) => {
        setExpandedItems((previouslyExpanded) => {
            const nextExpanded = new Set(previouslyExpanded);
            if (nextExpanded.has(itemId)) {
                nextExpanded.delete(itemId);
            } else {
                nextExpanded.add(itemId);
            }
            return nextExpanded;
        });
    };

    // Pagination change handler
    const handlePageChange = (page) => {
        setCurrentPage(page);
        setExpandedItems(new Set());
    };

    // Enhanced download handler that updates local state after download
    const handleDownloadWithCount = async (upload) => {
        handleDownload(upload);
        setTimeout(() => {
            setUploads((prevUploads) =>
                prevUploads.map((u) =>
                    u.downloadLink === upload.downloadLink
                        ? { ...u, count: (parseInt(u.count) || 0) + 1 }
                        : u
                )
            );
        }, 1000);
    };

    // Fetch downloads
    useEffect(() => {
        if (isAuthenticated) {
            // Authenticated users fetch all downloads
            fetch("/api/downloads")
                .then((response) => {
                    if (response.status === 401) {
                        setUploads([]);
                        return [];
                    }
                    return response.json();
                })
                .then((fetched) => {
                    if (fetched && fetched.length > 0) {
                        setUploads(fetched);
                        localStorage.setItem("uploads", JSON.stringify(fetched));
                    } else {
                        setUploads([]);
                    }
                })
                .catch(() => {
                    const uploadsText = localStorage.getItem("uploads");
                    if (uploadsText) {
                        setUploads(JSON.parse(uploadsText));
                    } else {
                        setUploads([]);
                    }
                });
        } else {
            // Unauthenticated users fetch only public downloads
            fetch("/api/public-downloads")
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error('Failed to fetch public downloads');
                    }
                })
                .then((fetched) => {
                    if (fetched && fetched.length > 0) {
                        setUploads(fetched);
                        localStorage.setItem("uploads", JSON.stringify(fetched));
                    } else {
                        setUploads([]);
                    }
                })
                .catch(() => {
                    const uploadsText = localStorage.getItem("uploads");
                    if (uploadsText) {
                        const allUploads = JSON.parse(uploadsText);
                        const publicUploads = allUploads.filter((u) => u.public === true || u.public === 'true');
                        setUploads(publicUploads);
                    } else {
                        setUploads([]);
                    }
                });
        }
    }, [isAuthenticated]);

    // Refetch after deletion
    const handleDeleteSuccess = () => {
        if (!isAuthenticated) return;
        fetch("/api/downloads")
            .then((response) => (response.status === 401 ? [] : response.json()))
            .then((fetched) => {
                if (fetched && fetched.length > 0) {
                    setUploads(fetched);
                    localStorage.setItem("uploads", JSON.stringify(fetched));
                } else {
                    setUploads([]);
                }
            })
            .catch(() => setUploads([]));
    };

    // Filter uploads: 'my-files' tab shows all user files, 'all' tab shows only public files
    const filteredUploads = activeTab === 'my-files'
        ? uploads.filter(upload => upload.username === currentUser)
        : uploads.filter(upload => upload.public === true || upload.public === 'true');
    
    // Ensure current page is within bounds whenever filtered results change
    useEffect(() => {
        const pages = Math.ceil(filteredUploads.length / itemsPerPage) || 1;
        if (currentPage > pages) {
            setCurrentPage(pages);
        } else if (currentPage < 1) {
            setCurrentPage(1);
        }
    }, [filteredUploads, itemsPerPage, currentPage]);
    


    // Apply sorting to filtered uploads
    const sortedUploads = sortUploads(filteredUploads, sortConfig.field, sortConfig.direction);

    // Total pages depends on filtered uploads
    const totalPages = Math.ceil(filteredUploads.length / itemsPerPage);

    // Get current page data
    const currentPageData = getCurrentPageData(sortedUploads);

    return (
        <main className="explore-main">
            <div className="explore-content">
                <h1 className="explore-title">// file explorer</h1>
                {!isAuthenticated && (
                    <div className="info-message">
                        <p>[INFO] viewing public files only. <a href="/">authenticate</a> to access all files and manage uploads.</p>
                    </div>
                )}

                <div className="explore-tabs">
                    <button
                        className={`explore-tab ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        <span className="tab-icon">./</span>
                        ALL FILES
                    </button>
                    {isAuthenticated && (
                        <button
                            className={`explore-tab ${activeTab === 'my-files' ? 'active' : ''}`}
                            onClick={() => setActiveTab('my-files')}
                        >
                            <span className="tab-icon">~/</span>
                            MY FILES
                        </button>
                    )}
                </div>

                <div className="uploads-section">
                    <h2 className="section-title">
                        {activeTab === 'all'
                            ? (isAuthenticated ? 'available downloads' : 'public files')
                            : 'my uploads'
                        }
                    </h2>

                    {/* Sort Controls */}
                    <div className="sort-controls">
                        <button
                            className={`sort-btn ${sortConfig.field === 'date' ? 'active' : ''}`}
                            onClick={() => handleSort('date')}
                        >
                            DATE {sortConfig.field === 'date' && (sortConfig.direction === 'asc' ? '[asc]' : '[desc]')}
                        </button>
                        <button
                            className={`sort-btn ${sortConfig.field === 'count' ? 'active' : ''}`}
                            onClick={() => handleSort('count')}
                        >
                            DOWNLOADS {sortConfig.field === 'count' && (sortConfig.direction === 'asc' ? '[asc]' : '[desc]')}
                        </button>
                    </div>
                    
                    <div className="files-grid">
                        {currentPageData.length > 0 ? (
                            currentPageData.map((upload, i) => {
                                const globalIndex = (currentPage - 1) * itemsPerPage + i;
                                const itemId = upload.downloadLink || `${upload.username}|${upload.date}|${upload.name}|${globalIndex}`;
                                const isExpanded = expandedItems.has(itemId);
                                
                                return (
                                    <div key={itemId} className="file-card">
                                        <div 
                                            className="file-card-header"
                                            onClick={() => toggleItemExpansion(itemId)}
                                        >
                                            <div className="file-info">
                                                <div className="file-name">
                                                    <span className="file-number">#{globalIndex + 1}</span>
                                                    <h3>{upload.name}</h3>
                                                </div>
                                                <div className="file-meta">
                                                    <span className="file-user">@ {upload.username}</span>
                                                    <span className="file-date">&gt; {upload.date}</span>
                                                    <span className="file-downloads">↓ {upload.count || 0}</span>
                                                </div>
                                            </div>
                                            <div className="file-actions">
                                                <button 
                                                    className={`expand-btn ${isExpanded ? 'rotated' : ''}`}
                                                    onClick={(e) => { e.stopPropagation(); toggleItemExpansion(itemId); }}
                                                    title={isExpanded ? "Collapse details" : "Expand details"}
                                                >
                                                    ▶
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {isExpanded && (
                                            <div className="file-card-details">
                                                <div className="detail-section">
                                                    <h4>File Description</h4>
                                                    <p className="file-text">
                                                        {upload.text.length < 1
                                                            ? "(---No text---)"
                                                            : upload.text}
                                                    </p>
                                                </div>
                                                
                                                <div className="detail-section">
                                                    <h4>Actions</h4>
                                                    <div className="action-buttons">
                                                        {upload.downloadLink ? (
                                                            <>
                                                                {isAuthenticated ? (
                                                                    <button
                                                                        className="download-btn"
                                                                        onClick={() => handleDownloadWithCount(upload)}
                                                                    >
                                                                        [ DOWNLOAD ]
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        className="download-btn disabled"
                                                                        onClick={() => alert("Please log in to download files")}
                                                                        title="Login required to download"
                                                                    >
                                                                        [ AUTH REQUIRED ]
                                                                    </button>
                                                                )}

                                                                <button
                                                                    className="qr-btn"
                                                                    onClick={() => showQRCodeOverlay(upload.downloadLink, upload.name)}
                                                                    title="Show QR code for download link"
                                                                >
                                                                    [ QR ]
                                                                </button>

                                                                <button
                                                                    className="copy-link-btn"
                                                                    onClick={() => handleCopyLink(upload.downloadLink)}
                                                                    title="Copy download link"
                                                                >
                                                                    {copiedLink === upload.downloadLink ? '[ ✓ copied ]' : '[ copy link ]'}
                                                                </button>

                                                                {activeTab === 'my-files' && (
                                                                    <>
                                                                        <button
                                                                            className={`keep-btn ${upload.keepForever ? 'keep-active' : ''}`}
                                                                            onClick={() => handleKeepForever(upload, !upload.keepForever)}
                                                                            title={upload.keepForever ? "Remove keep-forever (apply 90-day TTL)" : "Keep this file indefinitely"}
                                                                        >
                                                                            {upload.keepForever ? '[ ∞ keep: on ]' : '[ keep forever ]'}
                                                                        </button>
                                                                        <button
                                                                            className="delete-btn"
                                                                            onClick={() => handleDelete(upload, handleDeleteSuccess)}
                                                                        >
                                                                            [ DELETE ]
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="no-actions">No actions available</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="no-data">
                                {activeTab === 'all' 
                                    ? (isAuthenticated ? 'No downloads found' : 'No public files found') 
                                    : 'No uploads found'
                                }
                            </div>
                        )}
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                            >
                                |&lt; FIRST
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                &lt; PREV
                            </button>
                            <div className="page-info">
                                {currentPage} / {totalPages}
                            </div>
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                NEXT &gt;
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                LAST &gt;|
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="chat-section">
                    <h2 className="section-title">// chat terminal</h2>
                    <div className="chat-status">
                        {socket && socket.readyState === WebSocket.OPEN ? (
                            <span className="status-connected">[OK] connected</span>
                        ) : (
                            <span className="status-disconnected">[ERR] disconnected</span>
                        )}
                    </div>
                    {!isAuthenticated && (
                        <div className="chat-info">
                            <p>&gt; session id: <strong>{anonymousId}</strong></p>
                        </div>
                    )}
                    <div className="chat-container">
                        <div id="player-messages" className="messages-container">
                            {messages.map((message, index) => (
                                <div key={index} className="message-item">
                                    <span className="message-sender">{message.from}</span>: {message.msg}
                                </div>
                            ))}
                        </div>
                        <form onSubmit={handleSendMessage} className="message-form">
                            <input
                                type="text"
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                className="message-input"
                                placeholder={isAuthenticated ? `${currentUser || 'user'}@lightning:~$ ` : `${anonymousId}@lightning:~$ `}
                            />
                            <button type="submit" className="send-btn">[ SEND ]</button>
                        </form>
                    </div>
                </div>
            </div>
            
            {/* QR Code Overlay */}
            {showQRCode && (
                <div className="qr-overlay" onClick={closeQRCodeOverlay}>
                    <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="qr-header">
                            <h3>QR Code for: {qrCodeData.fileName}</h3>
                            <button className="qr-close-btn" onClick={closeQRCodeOverlay}>
                                ✕
                            </button>
                        </div>
                        <div className="qr-content">
                            {qrCodeData.url && (
                                <QRCodeSVG value={qrCodeData.url} size={300} className="qr-image" />
                            )}
                            <p className="qr-info">
                                Scan this QR code with your phone to download the file
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
